import { useState } from 'react';
import type { EmailPreview } from './useEmailTemplates';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Card, CardContent } from '../../../ui/card';
import {
  AlertCircle, FileQuestion, Loader2, Mail, Monitor, Paperclip, RefreshCw, Smartphone,
} from 'lucide-react';

interface EmailPreviewFrameProps {
  /** Heading for the current selection, or null when nothing is selected. */
  title: string | null;
  preview: EmailPreview | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  /** The selection exists but has no sample to render (not an error). */
  noSample?: boolean;
  /** Empty-state copy when nothing is selected. */
  emptyHint: string;
}

/**
 * Renders one email preview in a sandboxed iframe, with a desktop/mobile
 * width toggle and every state the panel can be in: nothing selected,
 * loading, failed, no sample, and the preview itself.
 */
export default function EmailPreviewFrame({
  title, preview, loading, error, onRetry, noSample = false, emptyHint,
}: EmailPreviewFrameProps) {
  const [viewMode, setViewMode] = useState<'desktop' | 'mobile'>('desktop');
  const width = viewMode === 'desktop' ? 640 : 375;

  return (
    <div className="flex-1 min-w-0 flex flex-col">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="min-w-0 text-sm">
          {title ? (
            <>
              <p className="font-medium truncate" data-testid="email-preview-title">{title}</p>
              {preview && (
                <p className="text-muted-foreground truncate">Subject: {preview.subject}</p>
              )}
            </>
          ) : (
            <p className="text-muted-foreground">{emptyHint}</p>
          )}
          {preview?.branding && (
            <Badge variant="secondary" className="mt-1" title="Branding applied to this preview">
              {preview.branding.source === 'company'
                ? `${preview.branding.company_name} branding`
                : 'Platform branding'}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1 border rounded-lg p-1 shrink-0">
          <Button
            variant={viewMode === 'desktop' ? 'default' : 'ghost'}
            size="sm" className="h-7 px-2" onClick={() => setViewMode('desktop')}
          >
            <Monitor className="h-4 w-4 mr-1" /> Desktop
          </Button>
          <Button
            variant={viewMode === 'mobile' ? 'default' : 'ghost'}
            size="sm" className="h-7 px-2" onClick={() => setViewMode('mobile')}
          >
            <Smartphone className="h-4 w-4 mr-1" /> Mobile
          </Button>
        </div>
      </div>

      {preview?.attachments && preview.attachments.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3 text-xs text-muted-foreground">
          <Paperclip className="h-3.5 w-3.5" />
          {preview.attachments.map((name) => (
            <Badge key={name} variant="outline" className="font-normal">{name}</Badge>
          ))}
        </div>
      )}

      <Card className="flex-1 overflow-hidden">
        <CardContent className="p-6 flex items-start justify-center h-full overflow-auto bg-muted/30 min-h-[520px]">
          {!title && (
            <div className="flex flex-col items-center justify-center text-center py-20">
              <Mail className="h-12 w-12 text-muted-foreground/30 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">Nothing selected</p>
              <p className="text-xs text-muted-foreground mt-1">{emptyHint}</p>
            </div>
          )}

          {title && noSample && (
            <div className="flex flex-col items-center justify-center text-center py-20 max-w-sm">
              <FileQuestion className="h-10 w-10 text-muted-foreground/60 mb-2" />
              <p className="text-sm font-medium">No preview sample for this email yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                This notification is sent with the standard branded layout, but there is no sample
                content to show here.
              </p>
            </div>
          )}

          {title && !noSample && loading && (
            <div className="flex items-center justify-center py-20" role="status" aria-label="Loading preview">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {title && !noSample && !loading && error && (
            <div className="flex flex-col items-center justify-center text-center py-20 max-w-sm">
              <AlertCircle className="h-10 w-10 text-red-500 mb-2" />
              <p className="text-sm font-medium">Preview could not be loaded</p>
              <p className="text-xs text-muted-foreground mt-1">{error}</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
                <RefreshCw className="h-4 w-4 mr-1" /> Try again
              </Button>
            </div>
          )}

          {title && !noSample && !loading && !error && preview && (
            <iframe
              srcDoc={preview.html}
              title={`Email preview: ${title}`}
              className="border border-border rounded-lg bg-white shadow-sm transition-all duration-300"
              style={{ width, maxWidth: '100%', height: 720 }}
              sandbox="allow-same-origin"
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
